import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Plus, PencilSimple, Trash, MagnifyingGlass, SquaresFour, Rows } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PillTabs } from "./lux";

/**
 * Generic CRUD list page.
 * Props:
 *  - endpoint: backend path (e.g. "/customers")
 *  - title: page-level heading (already shown in topbar)
 *  - emptyText: string
 *  - fields: [{ name, label, type, options, span, required, placeholder }]
 *      types: text, number, textarea, select, date, datetime, tags
 *  - columns: [{ key, label, render? }]
 *  - searchPlaceholder?: string
 *  - testid: prefix
 */
export default function CrudList({ endpoint, fields, columns, emptyText = "No records yet", testid, searchable = true, renderCard, filters = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(renderCard ? "cards" : "table");
  const [filterKey, setFilterKey] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = search && endpoint === "/customers" ? { search } : {};
      const { data } = await api.get(endpoint, { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (endpoint === "/customers") {
      const t = setTimeout(load, 300);
      return () => clearTimeout(t);
    }
    /* eslint-disable-next-line */
  }, [search]);

  const openCreate = () => {
    const blank = Object.fromEntries(fields.map((f) => [f.name, f.default ?? (f.type === "tags" ? [] : f.type === "number" ? 0 : f.type === "boolean" ? false : "")]));
    setForm(blank);
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (item) => {
    // Fields missing on older records fall back to their declared default so an
    // edit never silently flips a flag that was introduced later.
    const filled = Object.fromEntries(fields.map((f) => [f.name, item[f.name] ?? f.default ?? (f.type === "tags" ? [] : f.type === "number" ? 0 : f.type === "boolean" ? false : "")]));
    setForm(filled);
    setEditing(item);
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Coerce numeric strings
      for (const f of fields) {
        if (f.type === "number" && payload[f.name] !== "" && payload[f.name] !== null) {
          payload[f.name] = Number(payload[f.name]);
        }
        if (f.type === "tags" && typeof payload[f.name] === "string") {
          payload[f.name] = payload[f.name].split(",").map((s) => s.trim()).filter(Boolean);
        }
      }
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(endpoint, payload);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this record?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const searched = items.filter((it) => {
    if (!searchable || !search || endpoint === "/customers") return true;
    const hay = JSON.stringify(it).toLowerCase();
    return hay.includes(search.toLowerCase());
  });
  const activeFilter = filters.find((f) => f.key === filterKey);
  const filtered = activeFilter?.test ? searched.filter(activeFilter.test) : searched;

  return (
    <div className="space-y-6" data-testid={`${testid}-page`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {searchable && (
          <div className="relative w-full max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid={`${testid}-search-input`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="pl-10 h-10 rounded-full bg-white/[0.04] border-border"
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          {renderCard && (
            <div className="flex items-center gap-1 p-1 rounded-full border border-border" data-testid={`${testid}-view-toggle`}>
              {[["cards", SquaresFour], ["table", Rows]].map(([key, Icon]) => (
                <button key={key} onClick={() => setView(key)} data-testid={`${testid}-view-${key}`} title={key === "cards" ? "Card view" : "Table view"}
                  className={`p-2 rounded-full transition-colors ${view === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <Icon size={15} />
                </button>
              ))}
            </div>
          )}
          <Button data-testid={`${testid}-create-button`} onClick={openCreate} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus size={16} className="mr-1.5" /> New
          </Button>
        </div>
      </div>

      {filters.length > 0 && (
        <PillTabs
          tabs={filters.map((f) => ({
            key: f.key,
            label: f.label,
            count: f.test ? searched.filter(f.test).length : searched.length,
            testid: `${testid}-filter-${f.key}`,
          }))}
          value={filterKey}
          onChange={setFilterKey}
          size="sm"
          testid={`${testid}-filters`}
        />
      )}

      {view === "cards" && renderCard ? (
        loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => <div key={i} className="h-40 bg-card border border-border animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-border bg-card p-12 text-center text-muted-foreground">{emptyText}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger" data-testid={`${testid}-cards`}>
            {filtered.map((it) => (
              <div key={it.id} data-testid={`${testid}-card-${it.id}`}>
                {renderCard(it, { onEdit: () => openEdit(it), onDelete: () => remove(it.id) })}
              </div>
            ))}
          </div>
        )
      ) : (
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {columns.map((c) => (
                <th key={c.key} className="text-left px-4 py-3.5 label-mono font-normal">{c.label}</th>
              ))}
              <th className="text-right px-4 py-3.5 label-mono font-normal w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 1} className="p-12 text-center text-muted-foreground">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="p-12 text-center text-muted-foreground">{emptyText}</td></tr>
            ) : filtered.map((it) => (
              <tr key={it.id} className="border-b border-border/50 last:border-b-0 hover:bg-white/[0.03] transition-colors" data-testid={`${testid}-row-${it.id}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-4">{c.render ? c.render(it) : (it[c.key] ?? "—")}</td>
                ))}
                <td className="px-4 py-4 text-right">
                  <Button size="sm" variant="ghost" className="rounded-full" onClick={() => openEdit(it)} data-testid={`${testid}-edit-${it.id}`}>
                    <PencilSimple size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full" onClick={() => remove(it.id)} data-testid={`${testid}-delete-${it.id}`}>
                    <Trash size={14} className="text-destructive" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit record" : "Create new record"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            {fields.map((f) => (
              <div key={f.name} className={f.span === 2 ? "col-span-2" : "col-span-2 md:col-span-1"}>
                <Label className="label-mono mb-1.5 block">{f.label}{f.required && <span className="text-primary"> *</span>}</Label>
                {f.type === "textarea" ? (
                  <Textarea
                    data-testid={`${testid}-field-${f.name}`}
                    rows={3}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    placeholder={f.placeholder}
                    className="bg-input border-border"
                  />
                ) : f.type === "select" ? (
                  <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger data-testid={`${testid}-field-${f.name}`} className="bg-input border-border">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "boolean" ? (
                  <div className="flex items-center gap-3 pt-1">
                    <Switch
                      data-testid={`${testid}-field-${f.name}`}
                      checked={!!form[f.name]}
                      onCheckedChange={(v) => setForm({ ...form, [f.name]: !!v })}
                    />
                    <span className="text-xs text-muted-foreground">{f.hint || (form[f.name] ? "On" : "Off")}</span>
                  </div>
                ) : f.type === "tags" ? (
                  <Input
                    data-testid={`${testid}-field-${f.name}`}
                    value={Array.isArray(form[f.name]) ? form[f.name].join(", ") : (form[f.name] || "")}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    placeholder="Comma-separated"
                    className="bg-input border-border"
                  />
                ) : f.type === "photo" ? (
                  <PhotoField
                    testid={`${testid}-field-${f.name}`}
                    value={form[f.name] || ""}
                    uploadEndpoint={f.uploadEndpoint || "/vehicles/photo"}
                    onChange={(v) => setForm({ ...form, [f.name]: v })}
                  />
                ) : (
                  <Input
                    data-testid={`${testid}-field-${f.name}`}
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "datetime" ? "datetime-local" : "text"}
                    step={f.type === "datetime" ? 300 : undefined}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    placeholder={f.placeholder}
                    className="bg-input border-border"
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} data-testid={`${testid}-dialog-cancel`}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`${testid}-dialog-save`}>
              {saving ? "Saving…" : (editing ? "Save changes" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PhotoField({ testid, value, uploadEndpoint, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputId = `${testid}-file`;
  const handle = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(uploadEndpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url || data.image_url || "");
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-2">
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${value}` : value} alt="preview" className="h-16 w-24 object-cover rounded-md border border-border" />
          <Button type="button" size="sm" variant="outline" onClick={() => onChange("")} data-testid={`${testid}-clear`}>Remove</Button>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">No photo uploaded</div>
      )}
      <label htmlFor={inputId} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.06] hover:bg-white/[0.1] cursor-pointer text-sm transition-colors" data-testid={testid}>
        {uploading ? "Uploading…" : (value ? "Replace photo" : "Upload photo")}
      </label>
      <input id={inputId} type="file" accept="image/*" onChange={handle} className="hidden" disabled={uploading} data-testid={`${testid}-input`} />
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste an image URL"
        className="bg-input border-border text-xs"
        data-testid={`${testid}-url`}
      />
    </div>
  );
}
